import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Avatar } from '@mui/material';
import { motion } from 'framer-motion';

// Icons & UI Components
import Icon from '../../../../Assets/Icon/CustomIcon';
import { BottomSheetActions } from '../../../../Reducer/BottomSheetReducer';
import { PermitActions } from '../../../../Reducer/PermitReducer';

const getDisplayDuty = (person = {}) =>
  [person.rank, person.jobPosition].filter(Boolean).join(' · ') ||
  person.position ||
  '';

const getDisplayAffiliation = (person = {}) =>
  person.depNm || person.companyNm || '';

export default function Sign() {
  const dispatch = useDispatch();

  const { approvalLine } = useSelector((state) => state.permit);
  const { depHeads, safetyManager, observer } = approvalLine;

  const handleOpenBottomSheet = (selectionType) => {
    dispatch(
      BottomSheetActions.SetBottomSheetState({
        bottom: true,
        type: 'selectDepHeadId',
        data: { selectionType },
      })
    );
  };

  // 입회자는 선택사항 — 선택을 해제하면 입회 없이 진행
  const handleRemoveObserver = (e) => {
    e.stopPropagation();
    dispatch(PermitActions.setApprovalLine({ observer: null }));
  };

  const handleRemoveIntermediate = (e, headId) => {
    e.stopPropagation();
    const updatedHeads = depHeads.filter((head) => head.headId !== headId);
    dispatch(PermitActions.setApprovalLine({ depHeads: updatedHeads }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6 px-4"
    >
      {/* 1차 중간결재자 섹션 */}
      <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-primary text-xl">
              group
            </span>
            <h3 className="font-bold text-slate-800">1차 중간결재자</h3>
          </div>
          <span className="text-2xs rounded-full bg-slate-50 px-2.5 py-1 font-bold text-slate-400">
            최대 4명
          </span>
        </div>

        <div className="flex flex-wrap gap-3">
          {depHeads.map((head) => {
            const duty = getDisplayDuty(head);
            const affiliation = getDisplayAffiliation(head);

            return (
              <div
                key={head.headId}
                className="flex items-center gap-2 rounded-2xl border border-slate-100 bg-slate-50 py-1.5 pr-2 pl-1.5 shadow-sm"
              >
                <Avatar src={head.photo} sx={{ width: 30, height: 30 }}>
                  {!head.photo && <Icon.User size={16} />}
                </Avatar>
                <span className="min-w-0 text-sm font-semibold text-slate-700">
                  <span>
                    {head.headNm}
                    {duty && (
                      <span className="ml-1 text-xs font-medium text-slate-500">
                        {duty}
                      </span>
                    )}
                  </span>
                  {affiliation && (
                    <span className="block truncate text-2xs font-medium text-green-600">
                      {affiliation}
                    </span>
                  )}
                </span>
                <button
                  onClick={(e) => handleRemoveIntermediate(e, head.headId)}
                  className="flex items-center justify-center text-slate-400 transition-colors hover:text-red-500"
                >
                  <span className="material-symbols-outlined text-base">
                    close
                  </span>
                </button>
              </div>
            );
          })}

          {depHeads.length < 4 && (
            <button
              onClick={() => handleOpenBottomSheet('INTERMEDIATE')}
              className="text-primary flex h-10 w-10 items-center justify-center rounded-full bg-green-50 transition-all hover:bg-green-100 active:scale-90"
            >
              <span className="material-symbols-outlined">add</span>
            </button>
          )}
        </div>
      </section>

      {/* 2차 최종결재자 섹션 */}
      <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-xl text-orange-500">
            verified_user
          </span>
          <h3 className="font-bold text-slate-800">2차 최종결재자</h3>
        </div>

        {safetyManager ? (
          <div
            onClick={() => handleOpenBottomSheet('FINAL')}
            className="flex cursor-pointer items-center justify-between rounded-xl border border-orange-100 bg-orange-50/50 p-4"
          >
            <div className="flex items-center gap-4">
              <Avatar src={safetyManager.photo} sx={{ width: 36, height: 36 }}>
                {!safetyManager.photo && <Icon.User />}
              </Avatar>
              <div>
                <p className="text-sm font-bold text-slate-800">
                  {safetyManager.smNm}
                  {getDisplayDuty(safetyManager) && (
                    <span className="ml-1 text-xs font-medium text-slate-500">
                      {getDisplayDuty(safetyManager)}
                    </span>
                  )}
                </p>
                <p className="text-xs font-medium text-orange-600">
                  {getDisplayAffiliation(safetyManager)}
                </p>
              </div>
            </div>
            <span className="rounded-lg bg-white px-3 py-1 text-xs font-bold whitespace-nowrap text-orange-600 shadow-sm">
              변경
            </span>
          </div>
        ) : (
          <div
            onClick={() => handleOpenBottomSheet('FINAL')}
            className="group hover:border-primary/50 flex cursor-pointer items-center justify-between rounded-xl border-2 border-dashed border-slate-200 p-5 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="group-hover:text-primary flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition-colors">
                <span className="material-symbols-outlined">person_add</span>
              </div>
              <p className="text-sm font-bold text-slate-800">
                최종 승인권자 선택
              </p>
            </div>
            <span className="material-symbols-outlined text-slate-300">
              chevron_right
            </span>
          </div>
        )}
      </section>

      {/* 입회자 섹션 */}
      <section className="space-y-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
        <div className="flex items-center gap-2">
          <span className="material-symbols-outlined text-xl text-purple-500">
            visibility
          </span>
          <h3 className="font-bold text-slate-800">
            입회자
            <span className="ml-1 text-xs font-medium text-slate-400">
              (선택)
            </span>
          </h3>
        </div>

        {observer ? (
          <div
            onClick={() => handleOpenBottomSheet('WITNESS')}
            className="flex cursor-pointer items-center justify-between rounded-xl border border-purple-100 bg-purple-50/30 p-4"
          >
            <div className="flex items-center gap-4">
              <Avatar src={observer.photo} sx={{ width: 36, height: 36 }}>
                {!observer.photo && <Icon.User />}
              </Avatar>
              <div>
                <p className="text-sm font-bold text-slate-800">
                  {observer.name}
                  {getDisplayDuty(observer) && (
                    <span className="ml-1 text-xs font-medium text-slate-500">
                      {getDisplayDuty(observer)}
                    </span>
                  )}
                </p>
                <p className="text-xs font-medium text-purple-600">
                  {getDisplayAffiliation(observer)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleRemoveObserver}
                className="rounded-lg bg-white px-3 py-1 text-xs font-bold whitespace-nowrap text-slate-500 shadow-sm"
              >
                해제
              </button>
              <span className="rounded-lg bg-white px-3 py-1 text-xs font-bold whitespace-nowrap text-purple-600 shadow-sm">
                변경
              </span>
            </div>
          </div>
        ) : (
          <div
            onClick={() => handleOpenBottomSheet('WITNESS')}
            className="group hover:border-primary/50 flex cursor-pointer items-center justify-between rounded-xl border-2 border-dashed border-slate-200 p-5 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="group-hover:text-primary flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-400 transition-colors">
                <span className="material-symbols-outlined">person_search</span>
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">
                  입회 담당자 선택
                </p>
                <p className="mt-0.5 text-xs font-medium text-slate-400">
                  선택하지 않으면 입회 없이 진행됩니다.
                </p>
              </div>
            </div>
            <span className="material-symbols-outlined text-slate-300">
              chevron_right
            </span>
          </div>
        )}
      </section>
    </motion.div>
  );
}
